import Calendar from '@/components/calendar/Calendar';
import { ButtonCus, ButtonCusWithIcon } from '@/components/common/Button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { RadioGroup, RadioGroupItem } from '@/components/common/RadioGroup';
import { Textarea } from '@/components/common/Textarea';
import {
    COUPON_PATH,
    ekDomain,
    ROUTES,
    SUBMIT_MOMENT_DATE_YMD
} from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import {
    COUPON_TYPE,
    FORM_TYPE,
    FormType,
    PROMOTION_TYPE
} from '@/types/enums';
import { formatDateTime } from '@/utils/datetime';
import { isPathEk } from '@/utils/routes';
import { formatNumber } from '@/utils/formatNumber';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { cn } from '@/utils/utils';
import FormatNumberVer2 from '@/components/react-number-format/ReactNumberFormartver2';

interface CouponFormProps {
    type: FormType
}

export default observer(function CouponForm({ type }: CouponFormProps) {
    // const
    const isEdit = type === FORM_TYPE.Edit;
    const isCreate = type === FORM_TYPE.Create;

    // hook
    const { t } = useTranslation();
    const { couponPathType, id } = useParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isEk = isPathEk(pathname);

    // store
    const {
        couponStore: {
            coupon,
            createCoupon,
            getCouponDetail,
            updateCoupon,
            deleteCoupon,
            setObservable
        },
        modalStore: { showAlertModal, hideModal }
    } = useStore();

    // get id
    const newId = id ?? coupon?.id;

    // validate schema
    const validateSchema = yup.object().shape({
        couponType: yup.number().required('form_error_validate.required'),
        promotionType: yup.number().required('form_error_validate.required'),
        name: yup.string().required('form_error_validate.required'),
        code: yup.string().required('form_error_validate.required'),
        discountRate: yup
            .string().trim()
            .checkInputOnlyContainDigitsOrEmpty(t('form_error_validate.only_number'))
            .test(
                'min-value',
                t('form_error_validate.min', { min: 0 }),
                (val) => {
                    if (val == null || val === '') {
                        return true;
                    }
                    const num = Number(val);
                    return num >= 0;
                }
            )
            .when('promotionType', {
                is: (value) => {
                    return value === PROMOTION_TYPE.PERCENTAGE;
                },
                then: schema =>
                    schema.test(
                        'max-value',
                        t('form_error_validate.max', { max: 100 }),
                        (val) => {
                            if (val == null || val === '') {
                                return true;
                            }
                            const num = Number(val);
                            return num <= 100;
                        }
                    )
            })
            // .max(100, t('form_error_validate.min', { min: 100 }))
            .nullable()
            .typeError('form_error_validate.required')
            .required('form_error_validate.required'),
        couponQuantity: yup
            .string().trim()
            .checkInputOnlyContainDigitsOrEmpty(t('form_error_validate.only_number'))
            .test(
                'min-value',
                t('form_error_validate.min', { min: 0 }),
                (val) => {
                    if (val == null || val === '') {
                        return true;
                    }
                    const num = Number(val);
                    return num >= 0;
                }
            )
            .typeError('form_error_validate.required')
            .required('form_error_validate.required'),
        startDate: yup.date().required('form_error_validate.required'),
        expirationDate:
            couponPathType === COUPON_PATH.ONE_TIME ?
                yup.date().required('form_error_validate.required') :
                yup.date(),
        note: yup.string()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            couponType:
                couponPathType === COUPON_PATH.FOREVER ?
                    COUPON_TYPE.FOREVER_USE :
                    (couponPathType === COUPON_PATH.ONE_TIME ? COUPON_TYPE.ONE_TIME_USE : undefined),
            promotionType: PROMOTION_TYPE.PERCENTAGE,
            name: '',
            code: '',
            discountRate: '',
            couponQuantity: undefined,
            startDate: undefined,
            expirationDate: undefined,
            note: ''
        }
    });

    const watchStartDate = form.watch('startDate');
    const watchExpirationDate = form.watch('expirationDate');

    // lifecycle
    useEffect(() => {
        if (isEdit && id) {
            onFetchCouponById(Number(id));
        }

        return () => {
            setObservable('coupon', {});
        };
    }, [id]);

    // function
    const onFetchCouponById = async (id: number) => {
        const res = await flowResult(getCouponDetail(Number(id)));
        if (res) {
            const {
                couponType,
                promotionType,
                name,
                code,
                discountRate,
                couponQuantity,
                startDate,
                expirationDate,
                note
            } = res;
            form.reset({
                couponType,
                promotionType,
                name,
                code,
                discountRate,
                couponQuantity,
                startDate: startDate ? new Date(startDate) : undefined,
                expirationDate: expirationDate ? new Date(expirationDate) : undefined,
                note
            });
        }
    };

    const onSubmit = async (dataForm: FormData) => {
        const {
            couponType,
            promotionType,
            name,
            code,
            discountRate,
            couponQuantity,
            startDate,
            note
        } = dataForm;
        const dataSubmit: any = {
            couponType: Number(couponType),
            promotionType,
            name,
            code,
            discountRate,
            couponQuantity,
            startDate: formatDateTime(startDate, SUBMIT_MOMENT_DATE_YMD),
            note
        };

        if (couponPathType === COUPON_PATH.ONE_TIME) {
            dataSubmit.expirationDate = formatDateTime(dataForm?.expirationDate, SUBMIT_MOMENT_DATE_YMD);
        }

        switch (type) {
            case FORM_TYPE.Create: {
                const res = await flowResult(createCoupon(dataSubmit));
                if (res) {
                    showAlertModal({
                        id: 'add-coupon-success',
                        type: 'success',
                        content: t('messages.add_success'),
                        onClickXIcon: () =>
                            navigate(
                                isEk ?
                                    `/${ekDomain}/${ROUTES.couponSetting.hrefEK}` :
                                    ROUTES.couponSetting.href
                            )
                    });
                }
                break;
            }
            case FORM_TYPE.Edit: {
                const resEdit = await flowResult(
                    updateCoupon({
                        ...dataSubmit,
                        id: +newId
                    })
                );
                if (resEdit) {
                    showAlertModal({
                        id: 'update-coupon-success',
                        type: 'success',
                        content: t('messages.update_success'),
                        onClickXIcon: () =>
                            navigate(
                                isEk ?
                                    `/${ekDomain}/${ROUTES.couponSetting.hrefEK}` :
                                    ROUTES.couponSetting.href
                            )
                    });
                }
                break;
            }
            default:
                break;
        }
    };

    // api delete account by id
    const onDeleteCouponById = async (couponId: number) => {
        const res = await flowResult(deleteCoupon(couponId));
        if (res) {
            showAlertModal({
                id: 'delete-account-success',
                type: 'success',
                content: t('messages.delete_success'),
                onClickXIcon: () =>
                    navigate(
                        isEk ?
                            `/${ekDomain}/${ROUTES.couponSetting.hrefEK}` :
                            ROUTES.couponSetting.href
                    )
            });
        }
    };

    // handle click btn delete coupon
    const handleClickDeleteCoupon = () => {
        showAlertModal({
            id: 'delete-coupon',
            isConfirmModal: true,
            content: t('messages.confirm_delete'),
            onSave: () => {
                onDeleteCouponById(Number(id));
                hideModal('delete-coupon');
            }
        });
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className='layout-card flex flex-col gap-6'>
                    {isEdit && (
                        <div>
                            <span className='font-semibold text-[20px]'>
                                {`${t(
                                    'field.quantity_used'
                                )}/${t('field.release_quantity')}: `}
                            </span>
                            <span className='font-semibold text-[20px] text-red-1'>
                                {
                                    `${coupon.usedNumber ? formatNumber(coupon.usedNumber) : '0'
                                    }/${coupon.couponQuantity ?
                                        formatNumber(coupon.couponQuantity) :
                                        '0'
                                    }`
                                }
                            </span>
                        </div>
                    )}
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6'>
                        <FormField
                            control={form.control}
                            name='name'
                            render={({ field }) => (
                                <FormItem className='col-span-full'>
                                    <FormLabel required>{t('field.name_of_coupon')}</FormLabel>
                                    <FormControl>
                                        <Input
                                            type='text'
                                            placeholder={t('placeholder.enter')}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='code'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required>{t('field.code_of_coupon')}</FormLabel>
                                    <FormControl>
                                        <Input
                                            type='text'
                                            placeholder={t('placeholder.enter')}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='couponQuantity'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required>{t('field.number_of_coupons')}</FormLabel>
                                    <FormControl>
                                        <FormatNumberVer2
                                            decimalScale={0}
                                            placeholder={t('placeholder.enter')}
                                            statusDisplay='input'
                                            thousandSeparator={true}
                                            allowNegative={false}
                                            value={field.value}
                                            setvalueOfNumericFormat={field.onChange}
                                        // onPaste={e => e.preventDefault()}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='promotionType'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required>{t('field.discount')}</FormLabel>
                                    <FormControl>
                                        <RadioGroup
                                            key={field.value?.toString()}
                                            onValueChange={(value) => {
                                                field.onChange(value);
                                            }}
                                            value={
                                                field.value ?
                                                    field.value.toString() :
                                                    PROMOTION_TYPE.PERCENTAGE.toString()
                                            }
                                            className='flex items-center gap-14 w-full h-fit md:h-12'
                                        >
                                            <div className='flex items-center space-x-2'>
                                                <RadioGroupItem
                                                    value={PROMOTION_TYPE.PERCENTAGE.toString()}
                                                    id='percentage'
                                                />
                                                <Label
                                                    className='cursor-pointer font-normal'
                                                    htmlFor='percentage'
                                                >
                                                    %
                                                </Label>
                                            </div>
                                            <div className='flex items-center space-x-2'>
                                                <RadioGroupItem
                                                    value={PROMOTION_TYPE.MONEY.toString()}
                                                    id='money'
                                                />
                                                <Label
                                                    className='cursor-pointer font-normal'
                                                    htmlFor='money'
                                                >
                                                    {t('currency.currency_symbol')}
                                                </Label>
                                            </div>
                                        </RadioGroup>
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='discountRate'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className='hidden md:block h-[20px]' />
                                    <FormControl>
                                        <FormatNumberVer2
                                            decimalScale={0}
                                            placeholder={t('placeholder.enter')}
                                            statusDisplay='input'
                                            thousandSeparator={true}
                                            allowNegative={false}
                                            value={field.value}
                                            setvalueOfNumericFormat={field.onChange}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='startDate'
                            render={({ field, fieldState }) => (
                                <FormItem className={cn(couponPathType === COUPON_PATH.FOREVER && 'col-span-full')}>
                                    <FormLabel required>{t('field.start_date')}</FormLabel>
                                    <FormControl>
                                        <Calendar
                                            mode='single'
                                            selected={field.value}
                                            onSelect={(date) => {
                                                if (couponPathType === COUPON_PATH.ONE_TIME && watchExpirationDate && date > watchExpirationDate)
                                                    form.setValue('expirationDate', undefined);
                                                field.onChange(date);
                                            }}
                                            useIconRemove={true}
                                            className='w-full'
                                            disabled={[isCreate ? { before: new Date() } : false]}
                                            isInvalid={!!fieldState.error}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {couponPathType === COUPON_PATH.ONE_TIME && (
                            <FormField
                                control={form.control}
                                name='expirationDate'
                                render={({ field, fieldState }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.expirate_date')}</FormLabel>
                                        <FormControl>
                                            <Calendar
                                                mode='single'
                                                selected={field.value}
                                                onSelect={date => field.onChange(date)}
                                                useIconRemove={true}
                                                required
                                                disabled={[
                                                    {
                                                        before: watchStartDate || new Date()
                                                    }
                                                ]}
                                                isInvalid={!!fieldState.error}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}
                        <FormField
                            control={form.control}
                            name='note'
                            render={({ field }) => (
                                <FormItem className='col-span-full'>
                                    <Label className='text-[0.9375rem] font-medium'>
                                        {t('field.note')}
                                    </Label>
                                    <FormControl>
                                        <Textarea
                                            className='placeholder:text-placeholder-1'
                                            rows={5}
                                            maxLength={500}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                </div>
                <div className='mt-6 flex justify-center gap-4'>
                    {isEdit && (
                        <ButtonCusWithIcon
                            type='button'
                            variant='outline'
                            size='sm'
                            iconId='#icon-delete'
                            onClick={handleClickDeleteCoupon}
                        >
                            {t('button.delete')}
                        </ButtonCusWithIcon>
                    )}
                    {isCreate ?
                        (
                            <ButtonCus type='submit' variant='default' size='sm'>
                                {t('button.add')}
                            </ButtonCus>
                        ) :
                        (
                            <ButtonCusWithIcon
                                type='submit'
                                variant='default'
                                size='sm'
                                iconId='#icon-update'
                            >
                                {t('button.update')}
                            </ButtonCusWithIcon>
                        )}

                </div>
            </form>
        </Form>
    );
});
